import "@/App.css";
import { ErrorBoundary } from './components/ErrorBoundary';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import AccessCode from "./pages/AccessCode";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import TimesheetPage from "./pages/TimesheetPage";
import AdminUsers from "./pages/AdminUsers";
import AdminAssignments from "./pages/AdminAssignments";
import AdminSettings from "./pages/AdminSettings";
import AdminJugendaemter from "./pages/AdminJugendaemter";
import Profile from "./pages/Profile";
import TeacherConfirm from "./pages/TeacherConfirm";
import RechtsSeite from "./components/recht/RechtsSeite";
import { IMPRESSUM_TEXT } from "./inhalte/impressum";
import { DATENSCHUTZ_TEXT } from "./inhalte/datenschutz";

// Components
import Layout from "./components/Layout";
import { VersionBanner } from "./components/VersionBanner";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, fullAccessOnly = false }) => {
  const { isAuthenticated, loading, isAdmin, isAdminLike, isFullAccess, mustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required
  if (mustChangePassword) {
    return <ForcePasswordChange />;
  }

  if (fullAccessOnly && !isFullAccess()) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !isAdminLike()) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Dashboard Router - routes to correct dashboard based on role
const DashboardRouter = () => {
  const { isAdminLike } = useAuth();
  return isAdminLike() ? <AdminDashboard /> : <EmployeeDashboard />;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      
      <Route
        path="/forgot-password"
        element={<ForgotPassword />}
      />
      
      <Route
        path="/reset-password"
        element={<ForgotPassword />}
      />

      <Route
        path="/zugangscode"
        element={<AccessCode />}
      />

      <Route
        path="/teacher-confirm/:token"
        element={<TeacherConfirm />}
      />

      {/* Rechtsseiten: öffentlich, auch angemeldet erreichbar (bewusst ohne PublicRoute) */}
      <Route path="/impressum" element={<RechtsSeite text={IMPRESSUM_TEXT} testid="seite-impressum" />} />
      <Route path="/datenschutz" element={<RechtsSeite text={DATENSCHUTZ_TEXT} testid="seite-datenschutz" />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />

      <Route
        path="/timesheet"
        element={
          <ProtectedRoute>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/timesheet/:id"
        element={
          <ProtectedRoute>
            <TimesheetPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/assignments"
        element={
          <ProtectedRoute adminOnly>
            <AdminAssignments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/jugendaemter"
        element={
          <ProtectedRoute adminOnly>
            <AdminJugendaemter />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <VersionBanner />
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </BrowserRouter>
          <Toaster position="top-right" richColors duration={8000} closeButton expand visibleToasts={4} />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
